import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const LOGO = "https://customer-assets.emergentagent.com/job_821563ca-1f8b-4664-98fd-7932004d145f/artifacts/r4nyzz7e_Raknati.png";

export default function Navbar() {
  const { t, lang, toggleLang } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { href: "#home", label: t("nav.home") },
    { href: "#how", label: t("nav.how") },
    { href: "#features", label: t("nav.features") },
    { href: "#solutions", label: t("nav.solutions") },
    { href: "#partners", label: t("nav.partners") },
    { href: "#pricing", label: t("nav.pricing") },
    { href: "#investors", label: t("nav.investors") },
    { href: "#faq", label: t("nav.faq") },
    { href: "#contact", label: t("nav.contact") },
  ];

  const scrollTo = (href) => {
    setOpen(false);
    if (location.pathname !== "/") {
      window.location.href = "/" + href;
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-200 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-brand-line" : "bg-white/60 backdrop-blur-md"
      }`}
      data-testid="site-navbar"
    >
      <div className="container-x flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <img src={LOGO} alt="Raknati" className="h-9 w-9 object-contain" />
          <span className={`font-extrabold text-lg tracking-tight text-brand-charcoal ${lang === "ar" ? "font-arabicHead" : ""}`}>
            {lang === "ar" ? "ركنتي" : "Raknati"}
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => scrollTo(l.href)}
              className="px-3 py-2 text-sm font-medium text-brand-charcoal/70 hover:text-brand-charcoal transition-colors duration-200 rounded-full whitespace-nowrap"
              data-testid={`nav-link-${l.href.slice(1)}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <button
            onClick={toggleLang}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-brand-charcoal/80 hover:text-brand-green rounded-full border border-brand-line transition-colors duration-200 whitespace-nowrap"
            data-testid="nav-lang-toggle"
            aria-label="Toggle language"
          >
            <Globe className="h-4 w-4" />
            <span>{t("nav.lang")}</span>
          </button>
          <button
            onClick={() => scrollTo("#partners")}
            className="btn-outline hidden xl:inline-flex whitespace-nowrap"
            data-testid="nav-cta-pilot"
          >
            {t("nav.cta_pilot")}
          </button>
          <button
            onClick={() => scrollTo("#early-access")}
            className="btn-primary whitespace-nowrap"
            data-testid="nav-cta-early"
          >
            {t("nav.cta_early")}
          </button>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-brand-line"
          aria-label="Open menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-brand-line bg-white" data-testid="nav-mobile-panel">
          <div className="container-x py-4 flex flex-col gap-1">
            {links.map((l) => (
              <button
                key={l.href}
                onClick={() => scrollTo(l.href)}
                className="text-start px-3 py-3 rounded-xl text-brand-charcoal/80 hover:bg-brand-sand transition-colors duration-200"
                data-testid={`nav-mobile-${l.href.slice(1)}`}
              >
                {l.label}
              </button>
            ))}
            <div className="flex items-center gap-2 mt-2">
              <button onClick={toggleLang} className="btn-outline flex-1" data-testid="nav-mobile-lang">
                <Globe className="h-4 w-4" /> {t("nav.lang")}
              </button>
              <button onClick={() => scrollTo("#early-access")} className="btn-primary flex-1" data-testid="nav-mobile-cta-early">
                {t("nav.cta_early")}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
